/**
 * Serviço de autenticação
 * Responsável por login, logout, registro e gerenciamento de sessões
 * Conectado ao backend real via API
 */

import type { LoginRequestDTO, LoginResponseDTO, RegisterRequestDTO, UserDTO } from '@/types/auth';
import { API_ENDPOINTS } from '@/constants/api';
import { apiClient } from './apiClient';
import { translateAuthError } from '@/lib/errorTranslations';
import { removeAuthToken, getAuthToken } from '@/utils/auth';
import { establishClientSession, normalizeAuthRole } from '@/lib/postAuthSession';

/**
 * Faz login do usuário no backend
 */
export async function login(data: LoginRequestDTO): Promise<LoginResponseDTO> {
  try {
    console.log('[auth.login] 🌐 Making POST request to:', API_ENDPOINTS.AUTH.LOGIN);
    const response = await apiClient.post<any>(
      API_ENDPOINTS.AUTH.LOGIN,
      data,
    );

    console.log('[auth.login] ✅ API response received');
    console.log('[auth.login] Response data:', response.data);

    if (response.data) {
      // Backend wraps response in { success: true, data: {...} }
      const backendData = response.data.data || response.data;
      
      // Transform backend response to match frontend types
      const token = backendData.access_token || backendData.accessToken;
      
      // Get user and ensure role exists
      const user = backendData.user || {};
      const role = user.role || backendData.role; // Fallback to root role if not in user object
      
      console.log('[auth.login] 🔑 Auth Data:', { 
        hasToken: !!token, 
        hasUser: !!backendData.user,
        roleFound: role
      });

      if (!token) {
        throw new Error('Token não encontrado na resposta do servidor');
      }

      const transformedData: LoginResponseDTO = {
        user: {
          ...user,
          role: normalizeAuthRole(role) as UserDTO['role'],
        },
        accessToken: String(token).trim(),
        refreshToken: backendData.refresh_token || backendData.refreshToken,
      };

      if (typeof window !== 'undefined') {
        // Login: não marcar checklist como pendente se já concluído no backend
        establishClientSession({
          accessToken: transformedData.accessToken,
          user: transformedData.user,
          markPatientChecklistPending: false,
        });
      }
      
      console.log('[auth.login] ✅ Login successful, returning transformed data');
      return transformedData;
    }

    console.error('[auth.login] ❌ No response data!');
    throw new Error('Resposta inválida do servidor');
  } catch (error) {
    console.error('[auth.login] ❌ Error in login:', error);
    
    let message = 'Erro ao fazer login';
    
    if (error instanceof Error) {
      message = error.message;
      console.error('[auth.login] Error message:', message);
      console.error('[auth.login] Error stack:', error.stack);
    }
    
    // Translate error message to Portuguese
    const translatedMessage = translateAuthError(message);
    console.error('[auth.login] Translated error:', translatedMessage);
    throw new Error(translatedMessage);
  }
}

/**
 * Registra um novo usuário no backend
 */
export async function register(data: RegisterRequestDTO): Promise<LoginResponseDTO> {
  try {
    const response = await apiClient.post<any>(
      API_ENDPOINTS.AUTH.REGISTER,
      data,
    );

    if (!response.success) {
      throw new Error(response.error || response.message || 'Erro ao registrar');
    }

    if (!response.data) {
      throw new Error('Resposta inválida do servidor');
    }

    // Backend wraps response in { success: true, data: {...} }
    const backendData = response.data.data || response.data;

    // Transform backend response to match frontend types
    // Backend returns access_token, frontend expects accessToken
    const token = backendData.access_token || backendData.accessToken;

    if (!token) {
      throw new Error('Token não encontrado na resposta do servidor');
    }

    const user = backendData.user || {};
    const role = normalizeAuthRole(user.role || backendData.role || data.role);

    if (!user.id) {
      throw new Error('Dados do usuário incompletos na resposta do servidor');
    }

    const transformedData: LoginResponseDTO = {
      user: {
        ...user,
        role: role as UserDTO['role'],
      },
      accessToken: String(token).trim(),
      refreshToken: backendData.refresh_token || backendData.refreshToken,
    };

    if (typeof window !== 'undefined') {
      establishClientSession({
        accessToken: transformedData.accessToken,
        user: transformedData.user,
        // Paciente recém-cadastrado ainda precisa do onboarding
        markPatientChecklistPending: role === 'PATIENT',
        // Médico recém-cadastrado vai ao checklist de credenciais/Memed
        markDoctorOnboardingPending: role === 'DOCTOR',
      });
    }
    return transformedData;
  } catch (error) {
    let message = 'Erro ao registrar';

    if (error instanceof Error) {
      message = error.message;
    }

    // Translate error message to Portuguese
    const translatedMessage = translateAuthError(message);
    throw new Error(translatedMessage);
  }
}

/**
 * Obtém informações do usuário autenticado
 */
export async function getUser(userId: string): Promise<UserDTO> {
  try {
    const response = await apiClient.get<UserDTO>(
      API_ENDPOINTS.USERS.GET(userId),
    );

    if (response.data) {
      const raw = (response.data as any).data || response.data;
      return {
        ...raw,
        role: normalizeAuthRole(raw.role) as UserDTO['role'],
      };
    }

    throw new Error('Usuário não encontrado');
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Erro ao buscar usuário';
    throw new Error(message);
  }
}

/**
 * Carrega usuário salvo do localStorage
 */
export function getSavedUser(): UserDTO | null {
  if (typeof window === 'undefined') return null;

  const saved = localStorage.getItem('user');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (error) {
      console.error('Erro ao parsear usuário salvo:', error);
      return null;
    }
  }

  return null;
}

/**
 * Faz logout do usuário
 */
export function logout(): void {
  removeAuthToken();
}

/**
 * Busca usuário (fallback para versão anterior - mantém compatibilidade)
 */
export async function findUser(userId: string): Promise<UserDTO | null> {
  try {
    const user = await getUser(userId);
    return user;
  } catch (error) {
    return null;
  }
}

/**
 * Verifica se há token salvo
 */
export function hasToken(): boolean {
  return !!getAuthToken();
}

/**
 * Obtém token salvo
 */
export function getToken(): string | null {
  return getAuthToken();
}

export type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
};

export type ChangeEmailPayload = {
  newEmail: string;
  currentPassword: string;
};

export type ChangeEmailResult = {
  accessToken: string;
  user: UserDTO;
  message?: string;
};

/** Altera senha do usuário autenticado (exige senha atual). */
export async function changePassword(
  payload: ChangePasswordPayload,
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
    payload,
  );

  if (!response.success) {
    throw new Error(translateAuthError(response.error || 'Erro ao alterar senha'));
  }

  const data = (response.data as any)?.message
    ? response.data
    : (response as any).data || response;

  return {
    message: (data as { message?: string }).message || 'Senha alterada com sucesso.',
  };
}

/**
 * Altera e-mail do usuário autenticado (exige senha atual).
 * Atualiza token JWT + sessão local.
 */
export async function changeEmail(
  payload: ChangeEmailPayload,
): Promise<ChangeEmailResult> {
  const response = await apiClient.patch<any>(API_ENDPOINTS.AUTH.CHANGE_EMAIL, payload);

  if (!response.success) {
    throw new Error(translateAuthError(response.error || 'Erro ao alterar e-mail'));
  }

  const backendData = response.data?.data || response.data || {};
  const token = backendData.access_token || backendData.accessToken;
  const user = backendData.user;

  if (!token || !user) {
    throw new Error('Resposta inválida do servidor ao alterar e-mail');
  }

  const normalizedUser: UserDTO = {
    ...user,
    role: normalizeAuthRole(user.role) as UserDTO['role'],
  };

  establishClientSession({
    accessToken: String(token).trim(),
    user: normalizedUser,
    markPatientChecklistPending: false,
  });

  return {
    accessToken: String(token).trim(),
    user: normalizedUser,
    message: backendData.message || 'E-mail alterado com sucesso.',
  };
}

/**
 * Obter informações do usuário do backend
 */
export async function getUserInfo(userId: string): Promise<UserDTO> {
  return getUser(userId);
}

/**
 * Valida permissões de rota baseado no role
 */
export function hasPermissionForRoute(
  userRole: string,
  pathname: string,
): boolean {
  const roleRoutes: Record<string, string[]> = {
    ADMIN: ['/admin', '/users', '/dashboard'],
    DOCTOR: ['/consultas', '/dashboard'],
    PATIENT: ['/minhas-consultas', '/dashboard'],
  };

  const allowedRoutes = roleRoutes[userRole] || [];
  return allowedRoutes.some((route) => pathname.startsWith(route));
}

export const authService = {
  login,
  register,
  getUser,
  logout,
  changePassword,
  changeEmail,
  hasPermissionForRoute,
};
